import React, { useRef, useEffect } from "react";
import { gsap } from "gsap";
import "./ChromaGrid.css";
import { Link } from "react-router-dom";
import { Grid, Box, Typography } from "@mui/material";
import { motion } from "motion/react";

import styles from "../../Pages/Landing/Landing.module.css";
export interface ChromaItem {
  image: string;
  title: string;
  subtitle: string;
  handle?: string;
  location?: string;
  borderColor?: string;
  gradient?: string;
  url?: string;
}

export interface ChromaGridProps {
  items?: ChromaItem[];
  className?: string;
  radius?: number;
  columns?: number;
  rows?: number;
  damping?: number;
  fadeOut?: number;
  ease?: string;
}

type SetterFn = (v: number | string) => void;

export const ChromaGrid: React.FC<ChromaGridProps> = ({
  items,
  className = "",
  radius = 300,
  columns = 3,
  rows = 2,
  damping = 0.45,
  fadeOut = 0.6,
  ease = "power3.out",
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const fadeRef = useRef<HTMLDivElement>(null);
  const setX = useRef<SetterFn | null>(null);
  const setY = useRef<SetterFn | null>(null);
  const pos = useRef({ x: 0, y: 0 });

  const data = items?.length ? items : [];

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    setX.current = gsap.quickSetter(el, "--x", "px") as SetterFn;
    setY.current = gsap.quickSetter(el, "--y", "px") as SetterFn;
    const { width, height } = el.getBoundingClientRect();
    pos.current = { x: width / 2, y: height / 2 };
    setX.current(pos.current.x);
    setY.current(pos.current.y);
  }, []);

  const moveTo = (x: number, y: number) => {
    gsap.to(pos.current, {
      x,
      y,
      duration: damping,
      ease,
      onUpdate: () => {
        setX.current?.(pos.current.x);
        setY.current?.(pos.current.y);
      },
      overwrite: true,
    });
  };

  const handleMove = (e: React.PointerEvent) => {
    const r = rootRef.current!.getBoundingClientRect();
    moveTo(e.clientX - r.left, e.clientY - r.top);
    gsap.to(fadeRef.current, { opacity: 0, duration: 0.25, overwrite: true });
  };

  const handleLeave = () => {
    gsap.to(fadeRef.current, {
      opacity: 1,
      duration: fadeOut,
      overwrite: true,
    });
  };

  const handleCardClick = (url?: string) => {
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const handleCardMove: React.MouseEventHandler<HTMLElement> = (e) => {
    const card = e.currentTarget as HTMLElement;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    card.style.setProperty("--mouse-x", `${x}px`);
    card.style.setProperty("--mouse-y", `${y}px`);
  };
  const scaleVariants = {
    hidden: { opacity: 0, scale: 0.5 },
    visible: (custom: any) => ({
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: "easeOut", delay: 0.2 * custom },
    }),
  };
  return (
    <div
      ref={rootRef}
      className={`chroma-grid ${className}`}
      style={
        {
          "--r": `${radius}px`,
          "--cols": columns,
          "--rows": rows,
        } as React.CSSProperties
      }
      onPointerMove={handleMove}
      onPointerLeave={handleLeave}
    >
      {data.map((c, i) => (
        <Link to={c.url || ""} style={{ width: "100%", height: "100%" }}>
          <Box
            // item
            // xs={12}
            // sm={5.8}
            // md={3.8}
            sx={{ color: "white", position: "relative", mb: 1 }}
          >
            {" "}
            <motion.div
              variants={scaleVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.5 }}
              custom={i + 1}
            >
              {c.image ? (
                <img
                  src={c.image}
                  alt={c.title}
                  style={{ width: "100%", height: "100%" }}
                />
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    backgroundColor: "white",
                  }}
                />
              )}
              <Box
                sx={{
                  width: "100%",
                  backgroundColor: "black",
                  aspectRatio: 1,
                  opacity: 0.3,
                  position: "absolute",
                  top: 0,
                  left: 0,
                }}
              ></Box>
              <span className={styles["blog-content"]}>
                <p> {c.subtitle}</p>
                {/* <Typography variant="h5" component={"h2"}>
                  {c.title}
                </Typography> */}
              </span>{" "}
              <Typography
                variant="h6"
                sx={{ textAlign: "center", color: "#555" }}
                component="h1"
              >
                 {c.title}
              </Typography>
            </motion.div>
          </Box>
        </Link>
        // <div className="chroma-img-wrapper">
        //   <img src={c.image} alt={c.title} loading="lazy" />
        // </div>
        // <article
        //   key={i}
        //   className="chroma-card"
        //   onMouseMove={handleCardMove}
        //   onClick={() => handleCardClick(c.url)}
        //   style={
        //     {
        //       "--card-border": c.borderColor || "transparent",
        //       "--card-gradient": c.gradient,
        //       cursor: c.url ? "pointer" : "default",
        //     } as React.CSSProperties
        //   }
        // >

        //   <footer className="chroma-info">
        //     <h3 className="name">{c.title}</h3>
        //     {c.handle && <span className="handle">{c.handle}</span>}
        //     <p className="role">{c.subtitle}</p>
        //     {c.location && <span className="location">{c.location}</span>}
        //   </footer>
        // </article>
      ))}
      <div className="chroma-overlay" />
      <div ref={fadeRef} className="chroma-fade" />
    </div>
  );
};

export default ChromaGrid;
